import { Mail, ShieldCheck } from "lucide-react";
import {
  LegalPageLayout,
  LegalSection,
  SUPPORT_EMAIL,
} from "./LegalPageLayout";

const deletionRequestSubject = encodeURIComponent(
  "MediCorePay account deletion request",
);
const deletionRequestBody = encodeURIComponent(
  [
    "Please process my MediCorePay account deletion request.",
    "",
    "Request type: employee user account / pharmacy tenant account",
    "Registered email:",
    "Pharmacy or tenant name:",
    "Store (if applicable):",
    "",
    "I understand that MediCorePay may verify my identity and authority before processing this request.",
  ].join("\n"),
);
const deletionRequestUrl = `mailto:${SUPPORT_EMAIL}?subject=${deletionRequestSubject}&body=${deletionRequestBody}`;

export function AccountDeletionPage() {
  return (
    <LegalPageLayout
      title="Account and Data Deletion"
      description="This page explains how to request deletion of a MediCorePay user account or a pharmacy tenant account and which business records may need to be retained."
    >
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <ShieldCheck
            aria-hidden="true"
            className="mt-0.5 h-6 w-6 shrink-0 text-blue-700"
          />
          <div>
            <h2 className="font-black text-slate-950">
              This request page is available without signing in
            </h2>
            <p className="mt-1 text-slate-700">
              This process covers the MediCorePay Android app with package ID{" "}
              <code className="rounded bg-blue-100 px-1.5 py-0.5 font-mono text-sm font-semibold text-slate-950">
                com.mobile.medicorepay
              </code>
              . Never send a password, one-time code, access token, prescription
              or other sensitive pharmacy record in an account-deletion email.
            </p>
          </div>
        </div>
      </div>

      <LegalSection title="1. Start a deletion request">
        <p>
          Send the request from the email address associated with the account
          when possible. Identify whether the request is for an individual
          employee user account or the entire pharmacy tenant account, and
          include the pharmacy or tenant name and store name if applicable.
        </p>
        <a
          href={deletionRequestUrl}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-5 py-3 font-bold text-white shadow-sm hover:bg-blue-800"
        >
          <Mail aria-hidden="true" className="h-5 w-5" />
          Email an account deletion request
        </a>
        <p className="text-sm text-slate-600">
          If the email button does not open an email app, write to{" "}
          <a
            className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 hover:text-blue-900"
            href={`mailto:${SUPPORT_EMAIL}`}
          >
            {SUPPORT_EMAIL}
          </a>{" "}
          with the subject “MediCorePay account deletion request”.
        </p>
      </LegalSection>

      <LegalSection title="2. Individual employee user accounts">
        <p>
          MediCorePay user accounts are normally created and managed by a
          pharmacy or tenant administrator. An employee may ask that
          administrator to deactivate or remove the employee&apos;s access, or
          may use the email process above. MediCorePay may contact the relevant
          administrator to verify the request and protect the
          organisation&apos;s account.
        </p>
        <p>
          After verification, information used only for the individual login can
          be deleted or de-identified where appropriate. Pharmacy business
          records may continue to show that a user performed a stock count,
          receiving approval or other action because removing that attribution
          could damage the organisation&apos;s audit trail or conflict with
          retention obligations.
        </p>
      </LegalSection>

      <LegalSection title="3. Pharmacy tenant accounts">
        <p>
          A request to delete an entire pharmacy tenant account must come from
          an owner, administrator or other authorised representative. We will
          verify that authority before changing access or deleting organisation
          data. A tenant deletion request may affect every user, store and
          record in that pharmacy account.
        </p>
        <p>
          The pharmacy should first export or preserve any records it is
          required to keep. Depending on applicable contracts and law, sales,
          purchase, inventory, receiving, tax, compliance, security and audit
          records may need to be retained for a required period. Records that
          must be retained will be restricted to permitted purposes and deleted
          or de-identified when the applicable requirement ends. Backup copies
          are removed through normal backup rotation.
        </p>
      </LegalSection>

      <LegalSection title="4. What happens after a request">
        <ol className="list-decimal space-y-3 pl-6 marker:font-bold marker:text-blue-700">
          <li>
            We acknowledge the request using an appropriate contact channel.
          </li>
          <li>
            We verify the requester&apos;s identity and, for a tenant request,
            authority to act for the pharmacy. Additional information may be
            requested when reasonably necessary for verification.
          </li>
          <li>
            We identify account information eligible for deletion and any
            records that must be retained for the pharmacy, security, legal,
            regulatory, tax, dispute or audit purposes.
          </li>
          <li>
            We communicate the action taken, any applicable retention and the
            expected completion timing after verification.
          </li>
        </ol>
      </LegalSection>

      <LegalSection title="5. Information that may be retained">
        <p>
          A deletion request does not require MediCorePay to remove information
          that must reasonably be kept to comply with law, preserve pharmacy
          business and audit records, protect the service and its users, resolve
          a dispute, prevent fraud or demonstrate that a deletion request was
          completed. Retained information is limited to those purposes and
          remains subject to the safeguards described in the{" "}
          <a
            className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 hover:text-blue-900"
            href="/privacy"
          >
            Privacy Policy
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="6. Questions">
        <p>
          For questions about the request process or an existing request,
          contact{" "}
          <a
            className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 hover:text-blue-900"
            href={`mailto:${SUPPORT_EMAIL}?subject=MediCorePay%20account%20deletion%20question`}
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
